/**
 * Default brand — the bare-minimum BrandConfig used when a tenant has no
 * `brand_overrides` set. NOT a "named brand" you can pick from a registry —
 * just the seed every tenant starts from before their JSONB merges on top.
 *
 * Single source of truth for any tenant's brand is
 * `tenant.tenant_settings.brand_overrides`. This file exists so unconfigured
 * tenants render as something neutral and safe rather than blowing up.
 *
 * Naming intent: this brand should look "platform-default" — neither M² nor
 * Brij — so it's obvious when a tenant hasn't been configured yet. One
 * exception, by decision: agents of a brand without avatar sets wear the Brij
 * mark (`agentAvatars.mark`, see below).
 */

import type { BrandConfig } from './types';
import { BRIJ_AGENT_MARK } from './agent-mark';

// Inline neutral marks. defaultBrand is the pre-merge fallback every tenant
// starts from before their JSONB overrides apply; importing a named tenant's
// SVG (m2 or brij) here would silently leak that tenant's identity onto
// any unconfigured tenant. The marks below are intentionally generic — a
// platform "C" wordmark on neutral grey — so it is visually obvious when a
// tenant has not yet been styled.
const NEUTRAL_LOGO_SVG =
  'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 40">
      <rect width="160" height="40" rx="6" fill="#1f2937"/>
      <text x="80" y="26" font-family="system-ui, -apple-system, sans-serif" font-size="18" font-weight="600" fill="#f9fafb" text-anchor="middle">CorpBrain</text>
    </svg>`,
  );
const NEUTRAL_ICON_SVG =
  'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
      <rect width="40" height="40" rx="8" fill="#1f2937"/>
      <text x="20" y="27" font-family="system-ui, -apple-system, sans-serif" font-size="22" font-weight="700" fill="#f9fafb" text-anchor="middle">C</text>
    </svg>`,
  );
const logo = NEUTRAL_LOGO_SVG;
const icon = NEUTRAL_ICON_SVG;

export const defaultBrand: BrandConfig = {
  id: 'default',
  name: 'CorpBrain',
  // i18n key path, resolved by t(tagline, {defaultValue: tagline}) — a tenant
  // that sets its own slogan in the White-Label Editor still gets it verbatim,
  // because a miss falls through to the string itself.
  tagline: 'login.tagline',
  companyName: 'Brij Labs',
  companyUrl: 'https://brijlabs.ai/',
  pageTitle: 'CorpBrain',

  logo,
  icon,

  colors: {
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    secondary: '#60a5fa',
    secondaryLight: '#93c5fd',
    secondaryDark: '#1d4ed8',
    accent: '#3b82f6',
    gradientPrimary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  },

  lightMode: {
    bgPrimary: '#fafafa',
    bgSecondary: '#ffffff',
    bgTertiary: '#f5f5f5',
    bgElevated: '#eeeeee',
  },
  darkMode: {
    bgPrimary: '#0a0a0f',
    bgSecondary: '#12121a',
    bgTertiary: '#1a1a24',
    bgElevated: '#1e1e28',
  },

  fonts: {
    // Headings use Futura (locally bundled via brand/fonts.css, Medium +
    // Bold) so titles like the "SHALOM, …" hero render in the heavy brand
    // display face on every brand — including the default CorpBrain brand,
    // not just M²/JewishPro. Jost is the auto-applied fallback (see
    // apply-brand.ts). Body stays Inter: Futura is a display face and is
    // not meant for small running text.
    heading: 'Futura',
    body: 'Inter',
    mono: 'JetBrains Mono',
    // Jost loaded as the Futura fallback alongside Inter (body) + JetBrains Mono.
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Jost:wght@300;500;700&family=JetBrains+Mono:wght@400;500&display=swap',
  },

  // Labels are i18n key paths — see LoginPage.tsx (resolves via t()).
  // Tenant overrides may supply raw strings instead; t() falls through unchanged.
  loginFeatures: [
    { label: 'login.features.tenantAware', color: 'primary' },
    { label: 'login.features.knowledgeGrounded', color: 'accent' },
    { label: 'login.features.enterpriseSecurity', color: 'success' },
  ],

  // Agent coin palette — cycled across chatbot avatars so users can tell
  // bots apart at a glance. Indexed by a hash of the agent's name so the
  // same bot keeps the same color every render.
  //
  // EXACTLY the four M² brand-book families, and no more. A fifth purple
  // used to sit here to give 5-bot lineups unique swatches, but purple is
  // not an M² colour and there was never a purple texture behind it: the
  // picker stored the raw hex, `hexToTextureColor` returned null, and the
  // coin fell through to a name-hashed texture in one of these four hues.
  // So it promised a colour it could not render.
  agentColors: [
    '#6CC7EF', // blue
    '#C4D600', // green
    '#D50032', // red
    '#FAB600', // yellow
  ],

  // Agent avatars: no template sets here — the M² preset carries the
  // brand-book textures, and any tenant can upload its own in the White-Label
  // Editor (Agents & visuals → Avatar templates). A brand without sets draws
  // every agent as this mark in its colour. The one named-brand asset in this
  // file, by decision (Segev, 2026-11-01, Option C of
  // docs/BRIJ_AGENT_AVATARS_DESIGN.html): the platform's agents wear the Brij mark.
  agentAvatars: { mark: BRIJ_AGENT_MARK },

  // Decorative hanging half-circle on the home page — disabled for the
  // default brand. M² preset turns it on. When `color` is null, the
  // shape follows the currently-selected agent color.
  homeSemicircle: { enabled: false, color: null },

  // In-chat agent tinting — disabled by default so generic tenants keep
  // a clean neutral chat surface. M² preset turns it on.
  chat: { tintByAgent: false },

  // `email` (transactional email branding — sender name, subjects, logo,
  // colors for password-reset / invitation emails) is intentionally omitted:
  // every key falls back to the general brand values (`name`,
  // `colors.primary`, `logo`, `companyName`) and then platform defaults on
  // the backend, so a brand only sets it to diverge. Edited per tenant in
  // the White-Label Editor's Email section.
};
